import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from '@untitledui/icons/ChevronDown';

import { Button, ExternalLinkButton } from '@/components/base/buttons/button';
import { Card } from '@/components/base/card/card';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { teamPlanSummaryQueryOptions } from '@/routes/researchers/_authenticated/projects/$projectId/-launch/team-pricing-version';

import { BalanceActions } from './-balance-actions';
import { balanceLabels } from './-balance-labels';

export const Route = createFileRoute(
  '/researchers/_authenticated/billing/balances',
)({
  component: BillingBalancesPage,
  head: () => ({ meta: [{ title: t`Balances` }] }),
});

function BillingBalancesPage() {
  const { user, activeDashboard, env, hasLegacyPricing } =
    Route.useRouteContext();
  const navigate = Route.useNavigate();
  const teamId = activeDashboard.team ?? '';
  const organizationId = activeDashboard.organization ?? '';
  const classicBase = `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/billing`;
  const {
    data: summary,
    isPending,
    isError,
  } = useQuery({
    ...teamPlanSummaryQueryOptions(teamId, user.id),
    enabled: !!teamId,
  });
  const balances =
    summary?.plan && summary.checkPoint
      ? balanceLabels(summary.plan, summary.checkPoint)
      : null;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-display-xs font-semibold text-primary">
          <Trans>Balances</Trans>
        </h1>
        <div className="flex flex-wrap gap-2">
          {hasLegacyPricing && (
            <Dropdown.Root>
              <Button size="sm" color="secondary" iconTrailing={ChevronDown}>
                <Trans>View legacy activity</Trans>
              </Button>
              <Dropdown.Popover>
                <Dropdown.Menu
                  aria-label={t`Legacy activity`}
                  onAction={() =>
                    void navigate({ to: '/researchers/billing/spend-reports' })
                  }
                >
                  <Dropdown.Item id="spend-reports" label={t`Spend reports`} />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          )}
          <ExternalLinkButton
            href={`${classicBase}/balances`}
            target="_blank"
            size="sm"
            color="secondary"
          >
            <Trans>Manage in classic billing</Trans>
          </ExternalLinkButton>
        </div>
      </div>
      {isPending && (
        <p role="status" className="text-sm text-secondary">
          <Trans>Loading balances…</Trans>
        </p>
      )}
      {isError && (
        <p role="alert" className="text-sm text-error-primary">
          <Trans>
            Could not load balances. You can still manage them in classic
            billing.
          </Trans>
        </p>
      )}
      {!isPending && !isError && !balances && (
        <p role="status" className="text-sm text-secondary">
          <Trans>
            No pricing plan is available for this team. See classic billing for
            your balance.
          </Trans>
        </p>
      )}
      {balances && (
        <div className="grid gap-4 lg:grid-cols-2">
          <BalanceCard
            title={t`Recruitment`}
            available={balances.recruitmentAvailable}
            reserved={balances.recruitmentReserved}
          />
          <BalanceCard
            title={t`Incentive`}
            available={balances.incentiveAvailable}
            reserved={balances.incentiveReserved}
          />
        </div>
      )}
      {teamId && organizationId && summary?.plan && summary.checkPoint && (
        <BalanceActions
          teamId={teamId}
          organizationId={organizationId}
          userId={user.id}
          isEmailVerified={user.isEmailVerified ?? false}
          summary={summary}
        />
      )}
    </>
  );
}

function BalanceCard({
  title,
  available,
  reserved,
}: {
  title: string;
  available: string;
  reserved: string;
}) {
  return (
    <Card className="space-y-4">
      <h2 className="text-md font-semibold text-primary">{title}</h2>
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-secondary">
            <Trans>Available</Trans>
          </dt>
          <dd className="font-medium text-primary">{available}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-secondary">
            <Trans>On hold</Trans>
          </dt>
          <dd className="font-medium text-primary">{reserved}</dd>
        </div>
      </dl>
    </Card>
  );
}
